<template>
    <v-container class="py-7">
        <v-row>
            <v-col :cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">Resumo da turma</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbItems"></v-breadcrumbs>
                    </div>
                    <div class="button-group">
                        <v-btn type="button" elevation="0" color="primary" dark :to="`/turmas/${classId}`">
                            Editar turma
                        </v-btn>
                        <v-btn type="button" elevation="0" color="primary" outlined to="/turmas">Voltar</v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row v-if="loading">
            <v-col cols="12">
                <v-skeleton-loader type="article, actions"></v-skeleton-loader>
            </v-col>
        </v-row>

        <template v-else>
            <v-row>
                <v-col cols="12">
                    <v-card class="class-summary-hero">
                        <div>
                            <span class="class-summary-eyebrow">Turma</span>
                            <h1>{{ classData.name || "Turma sem nome" }}</h1>
                            <p>{{ classData.description || "Resumo operacional da turma no ano vigente." }}</p>
                        </div>

                        <div class="class-summary-meta">
                            <div>
                                <small>Ensino</small>
                                <strong>{{ classData.education ? classData.education.name : "N/A" }}</strong>
                            </div>
                            <div>
                                <small>Sala</small>
                                <strong>{{ classData.classRoom ? classData.classRoom.name : "N/A" }}</strong>
                            </div>
                            <div>
                                <small>Período</small>
                                <strong>{{ classData.day_shift | enum(dayPeriods, "id", "name") }}</strong>
                            </div>
                            <div>
                                <small>Ano vigente</small>
                                <strong>{{ schoolYear.year || "N/A" }}</strong>
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" md="2" v-for="item in kpis" :key="item.label">
                    <v-card class="summary-kpi">
                        <v-icon>{{ item.icon }}</v-icon>
                        <div>
                            <strong>{{ item.value }}</strong>
                            <span>{{ item.label }}</span>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" lg="9">
                    <v-card class="card-form-outlined summary-panel">
                        <v-card-title class="class-section-title">
                            <div>
                                <span>Grade semanal</span>
                                <small>Distribuição recorrente das aulas da turma</small>
                            </div>
                            <v-chip label small color="primary" text-color="white">
                                {{ schedules.length }} aula(s)
                            </v-chip>
                        </v-card-title>
                        <v-card-text>
                            <div class="weekly-grid" v-if="weeklySchedule.length">
                                <section v-for="group in weeklySchedule" :key="group.day" class="weekly-day">
                                    <strong>{{ group.shortLabel }}</strong>
                                    <div v-if="group.items.length" class="weekly-lessons">
                                        <article v-for="schedule in group.items" :key="schedule.id">
                                            <span>{{ schedule.start_time }} – {{ schedule.end_time }}</span>
                                            <b>{{ schedule.course ? schedule.course.name : "Componente" }}</b>
                                            <small>{{ schedule.teacher ? schedule.teacher.name : "Sem professor" }}</small>
                                        </article>
                                    </div>
                                    <span v-else class="weekly-empty">Sem aulas</span>
                                </section>
                            </div>
                            <div v-else class="summary-empty">
                                <v-icon>mdi-calendar-blank-outline</v-icon>
                                <span>Nenhuma aula cadastrada na grade semanal.</span>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" lg="3">
                    <v-card class="card-form-outlined summary-panel">
                        <v-card-title class="class-section-title">
                            <div>
                                <span>Pontos de atenção</span>
                                <small>O que falta para concluir a configuração</small>
                            </div>
                        </v-card-title>
                        <v-card-text class="attention-list">
                            <div v-for="warning in academicWarnings" :key="warning.text" :class="warning.level">
                                <v-icon small>{{ warning.icon }}</v-icon>
                                <span>{{ warning.text }}</span>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" lg="4">
                    <v-card class="card-form-outlined summary-panel">
                        <v-card-title class="card-group-title">Horários</v-card-title>
                        <v-card-text>
                            <div class="summary-info-list">
                                <div>
                                    <span>Início da aula</span>
                                    <strong>{{ classData.start_time || "N/A" }}</strong>
                                </div>
                                <div>
                                    <span>Fim da aula</span>
                                    <strong>{{ classData.end_time || "N/A" }}</strong>
                                </div>
                                <div>
                                    <span>Intervalo</span>
                                    <strong>{{ intervalLabel }}</strong>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" lg="8">
                    <v-card class="card-form-outlined summary-panel">
                        <v-card-title class="class-section-title">
                            <div>
                                <span>Componentes e docentes</span>
                                <small>Oferta pedagógica válida para o ano selecionado</small>
                            </div>
                        </v-card-title>
                        <v-card-text>
                            <div class="component-summary-list" v-if="components.length">
                                <div v-for="component in components" :key="component.id">
                                    <div>
                                        <strong>{{ component.course ? component.course.name : "Componente" }}</strong>
                                        <small>{{ componentWorkload(component) }}</small>
                                    </div>
                                    <span>
                                        {{ componentTeachers(component) }}
                                    </span>
                                </div>
                            </div>
                            <span v-else>Nenhum componente ofertado para esta turma.</span>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card class="table-card">
                        <v-card-title class="table-card-title">Alunos matriculados</v-card-title>
                        <v-card-text>
                            <v-data-table
                                :headers="studentHeaders"
                                :items="students"
                                :items-per-page="10"
                                class="app-table"
                                no-data-text="Nenhum aluno matriculado nesta turma"
                                :footer-props="studentFooterOptions"
                            >
                                <template v-slot:[`item.name`]="{ item }">
                                    <div class="student-cell">
                                        <v-avatar size="34">
                                            <v-img v-if="item.document_url" :src="item.document_url"></v-img>
                                            <v-icon v-else>mdi-account-school-outline</v-icon>
                                        </v-avatar>
                                        <div>
                                            <strong>{{ item.name || "Aluno sem nome" }}</strong>
                                            <small>{{ item.registration || "Sem matrícula" }}</small>
                                        </div>
                                    </div>
                                </template>
                                <template v-slot:[`item.email`]="{ item }">
                                    {{ item.email || "N/A" }}
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card class="table-card">
                        <v-card-title class="table-card-title">Avaliações vinculadas</v-card-title>
                        <v-card-text>
                            <v-data-table
                                :headers="assessmentHeaders"
                                :items="assessments"
                                :items-per-page="5"
                                class="app-table"
                                no-data-text="Nenhuma avaliação vinculada à turma"
                                :footer-props="footerOptions"
                            >
                                <template v-slot:[`item.course`]="{ item }">
                                    {{ item.course ? item.course.name : "N/A" }}
                                </template>
                                <template v-slot:[`item.teacher`]="{ item }">
                                    {{ item.teacher ? item.teacher.name : "N/A" }}
                                </template>
                                <template v-slot:[`item.end_date`]="{ item }">
                                    {{ item.end_date | defaultDate("DD/MM/YYYY") }}
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script>
import axios from "axios";
import dayPeriods from "@/common/data/day-periods.data";
import weekDays from "@/common/data/week-days.data";

export default {
    name: "ClassSummary",

    data: () => ({
        loading: false,
        classData: {},
        schoolYear: {},
        students: [],
        schedules: [],
        assessments: [],
        teachers: [],
        courses: [],
        components: [],
        stats: {},
        dayPeriods,
        weekDays,
        studentHeaders: [
            { text: "Aluno", value: "name" },
            { text: "E-mail", value: "email" },
        ],
        assessmentHeaders: [
            { text: "Avaliação", value: "title" },
            { text: "Componente", value: "course" },
            { text: "Professor", value: "teacher" },
            { text: "Entrega", value: "end_date" },
        ],
        footerOptions: {
            itemsPerPageText: "Itens por página:",
            itemsPerPageOptions: [5, 10, 20],
            pageText: "{0}-{1} de {2}",
        },
        studentFooterOptions: {
            itemsPerPageText: "Itens por página:",
            itemsPerPageOptions: [10, 20, 50, 100],
            pageText: "{0}-{1} de {2}",
        },
    }),

    computed: {
        classId() {
            return this.$route.params.id;
        },

        breadcrumbItems() {
            return [
                { text: "Homepage", disabled: false, to: "/home", exact: true },
                { text: "Turmas", disabled: false, to: "/turmas", exact: true },
                { text: "Resumo da turma", disabled: true, exact: true },
            ];
        },

        intervalLabel() {
            if (!this.classData.interval_start_time || !this.classData.interval_end_time) return "N/A";

            return `${this.classData.interval_start_time} - ${this.classData.interval_end_time}`;
        },

        kpis() {
            return [
                { label: "Alunos", value: this.stats.students || 0, icon: "mdi-account-school-outline" },
                { label: "Professores", value: this.stats.teachers || 0, icon: "mdi-account-tie-outline" },
                { label: "Componentes", value: this.stats.courses || 0, icon: "mdi-book-open-page-variant-outline" },
                { label: "Aulas semanais", value: this.stats.weekly_lessons || 0, icon: "mdi-calendar-clock-outline" },
                { label: "Avaliações", value: this.stats.assessments || 0, icon: "mdi-clipboard-text-outline" },
            ];
        },

        groupedSchedules() {
            return this.weekDays
                .map((day) => ({
                    day: day.id,
                    dayLabel: day.name,
                    items: this.schedules.filter((schedule) => Number(schedule.day) === Number(day.id)),
                }))
                .filter((group) => group.items.length);
        },

        weeklySchedule() {
            return this.weekDays
                .filter((day) => Number(day.id) >= 1 && Number(day.id) <= 5)
                .map((day) => ({
                    day: day.id,
                    shortLabel: String(day.name || "").replace("-feira", ""),
                    items: this.schedules
                        .filter((schedule) => Number(schedule.day) === Number(day.id))
                        .sort((a, b) => String(a.start_time).localeCompare(String(b.start_time))),
                }));
        },

        academicWarnings() {
            const warnings = [];
            const withoutTeacher = this.components.filter(
                (component) => !(component.teachers || []).length
            ).length;
            const withoutSchedule = this.components.filter(
                (component) => !(component.schedules || []).length
            ).length;
            const withoutWorkload = this.components.filter(
                (component) => !component.weekly_workload && !component.annual_workload
            ).length;

            if (withoutTeacher) {
                warnings.push({
                    text: `${withoutTeacher} componente(s) sem professor`,
                    icon: "mdi-account-alert-outline",
                    level: "warning",
                });
            }
            if (withoutSchedule) {
                warnings.push({
                    text: `${withoutSchedule} componente(s) sem horário`,
                    icon: "mdi-calendar-alert-outline",
                    level: "warning",
                });
            }
            if (withoutWorkload) {
                warnings.push({
                    text: `${withoutWorkload} componente(s) sem carga horária`,
                    icon: "mdi-clock-alert-outline",
                    level: "info",
                });
            }
            if (!warnings.length) {
                warnings.push({
                    text: "Configuração pedagógica completa",
                    icon: "mdi-check-circle-outline",
                    level: "success",
                });
            }
            return warnings;
        },
    },

    created() {
        this.load();
    },

    methods: {
        async load() {
            this.loading = true;

            try {
                const result = await axios.get(`/api/v1/classes/${this.classId}/summary`);
                const data = result.data.content || {};

                this.classData = data.class || {};
                this.schoolYear = data.school_year || {};
                this.students = data.students || [];
                this.schedules = data.schedules || [];
                this.assessments = data.assessments || [];
                this.teachers = data.teachers || [];
                this.courses = data.courses || [];
                this.components = data.components || [];
                this.stats = data.stats || {};
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 3000 });
            } finally {
                this.loading = false;
            }
        },

        componentTeachers(component) {
            const names = (component.teachers || [])
                .map((assignment) => assignment.employee && assignment.employee.name)
                .filter(Boolean);
            return names.length ? names.join(", ") : "Sem professor";
        },

        componentWorkload(component) {
            if (component.weekly_workload) return `${component.weekly_workload} aula(s) por semana`;
            if (component.annual_workload) return `${component.annual_workload} aula(s) no ano`;
            return "Carga horária não informada";
        },
    },
};
</script>

<style scoped lang="scss">
.class-summary-hero {
    align-items: flex-start;
    background: #0f172a !important;
    border-radius: 8px;
    color: #ffffff;
    display: flex;
    justify-content: space-between;
    gap: 24px;
    padding: 24px;
}

.class-summary-eyebrow {
    color: #93c5fd;
    display: block;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    margin-bottom: 8px;
    text-transform: uppercase;
}

.class-summary-hero h1 {
    font-size: 30px;
    font-weight: 900;
    line-height: 36px;
    margin: 0 0 8px;
}

.class-summary-hero p {
    color: #cbd5e1;
    font-size: 14px;
    margin: 0;
}

.class-summary-meta {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(2, minmax(130px, 1fr));
    min-width: 360px;
}

.class-summary-meta div,
.summary-kpi,
.summary-info-list div {
    background: #ffffff;
    border: 1px solid #e5eaf2;
    border-radius: 8px;
}

.class-summary-meta div {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.12);
    padding: 12px;
}

.class-summary-meta small,
.summary-info-list span {
    color: #94a3b8;
    display: block;
    font-size: 11px;
    font-weight: 700;
    margin-bottom: 4px;
}

.class-summary-meta strong {
    color: #ffffff;
    font-size: 14px;
}

.summary-kpi {
    align-items: center;
    display: flex;
    gap: 14px;
    min-height: 86px;
    padding: 16px;
}

.summary-kpi .v-icon {
    background: #edf4ff;
    border-radius: 8px;
    color: var(--v-primary-base);
    padding: 8px;
}

.summary-kpi strong {
    color: #0f172a;
    display: block;
    font-size: 24px;
    font-weight: 900;
}

.summary-kpi span {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
}

.summary-panel {
    min-height: 100%;
}

.class-section-title {
    align-items: flex-start;
    display: flex;
    justify-content: space-between;
}

.class-section-title span,
.class-section-title small {
    display: block;
}

.class-section-title small {
    color: #64748b;
    font-size: 11px;
    font-weight: 500;
    margin-top: 2px;
}

.weekly-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(5, minmax(130px, 1fr));
    overflow-x: auto;
}

.weekly-day {
    min-width: 130px;
}

.weekly-day > strong {
    color: #475569;
    display: block;
    font-size: 12px;
    margin-bottom: 8px;
    text-transform: capitalize;
}

.weekly-lessons {
    display: grid;
    gap: 8px;
}

.weekly-lessons article {
    border: 1px solid #dce5f0;
    border-left: 3px solid var(--v-primary-base);
    border-radius: 6px;
    min-height: 90px;
    padding: 10px;
}

.weekly-lessons article span,
.weekly-lessons article b,
.weekly-lessons article small {
    display: block;
}

.weekly-lessons article span,
.weekly-lessons article small,
.weekly-empty {
    color: #64748b;
    font-size: 11px;
}

.weekly-lessons article b {
    color: #0f172a;
    font-size: 13px;
    margin: 4px 0;
}

.weekly-empty {
    align-items: center;
    border: 1px dashed #dce5f0;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    min-height: 90px;
}

.attention-list {
    display: grid;
    gap: 8px;
}

.attention-list > div {
    align-items: flex-start;
    border: 1px solid #e5eaf2;
    border-radius: 6px;
    display: flex;
    font-size: 12px;
    gap: 8px;
    padding: 10px;
}

.attention-list .warning {
    background: #fff8ed;
    border-color: #f5d5a3;
    color: #92400e;
}

.attention-list .info {
    background: #f3f7fc;
    color: #475569;
}

.attention-list .success {
    background: #ecfdf5;
    border-color: #a7f3d0;
    color: #047857;
}

.component-summary-list {
    display: grid;
    gap: 8px;
}

.component-summary-list > div {
    align-items: center;
    border-bottom: 1px solid #edf1f6;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    padding: 9px 0;
}

.component-summary-list strong,
.component-summary-list small {
    display: block;
}

.component-summary-list small,
.component-summary-list > div > span {
    color: #64748b;
    font-size: 11px;
}

.summary-empty {
    align-items: center;
    color: #64748b;
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    min-height: 150px;
}

.summary-info-list {
    display: grid;
    gap: 10px;
}

.summary-info-list div {
    padding: 14px;
}

.summary-info-list strong {
    color: #0f172a;
    font-size: 15px;
}

.summary-chip-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.student-cell {
    align-items: center;
    display: flex;
    gap: 10px;
}

.student-cell strong,
.student-cell small {
    display: block;
}

.student-cell small {
    color: #64748b;
    font-size: 11px;
}

.schedule-list {
    display: grid;
    gap: 14px;
}

.schedule-day > span {
    color: #64748b;
    display: block;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    margin-bottom: 8px;
    text-transform: uppercase;
}

.schedule-item {
    border: 1px solid #e5eaf2;
    border-radius: 8px;
    margin-bottom: 8px;
    padding: 10px 12px;
}

.schedule-item strong,
.schedule-item small {
    display: block;
}

.schedule-item small {
    color: #64748b;
    margin-top: 2px;
}

@media only screen and (max-width: 960px) {
    .class-summary-hero {
        flex-direction: column;
    }

    .class-summary-meta {
        grid-template-columns: 1fr;
        min-width: 0;
        width: 100%;
    }

    .weekly-grid {
        grid-template-columns: repeat(5, 180px);
    }

    .component-summary-list > div {
        align-items: flex-start;
        flex-direction: column;
        gap: 4px;
    }
}
</style>
